import { derived, writable } from 'svelte/store';
import { api } from '@/utils/api';

// ============================================================
// 🔴 이 스토어는 반드시 `@/utils/api` 를 경유한다. 생 `fetch` 로 되돌리지 말 것.
//
// 서버의 v1 API 는 예외 없이 `render_success(data:)` 봉투를 씌운다
// (app/controllers/api/v1/base_controller.rb). 생 fetch 로 받으면 최상위에
// `notifications` 가 없어(`{ data: { notifications } }`) 목록이 조용히 빈 채로
// 남는다 — 예외는 catch 가 삼키고 화면에는 "알림이 없습니다" 만 남아서, 알림이
// 오지 않는 것과 구분되지 않는다. (2026-08-09 실측: 이 상태로 배포돼 있었다.)
//
// api 클라이언트는 세 가지를 대신 해 준다:
//   1. 봉투 해제 — 본문을 결과에 spread 하므로 `res.data` 가 곧 페이로드다
//   2. snake_case → camelCase 변환 — 그래서 아래 타입이 `createdAt` 이다
//   3. `X-CSRF-Token` 부착 — 생 fetch 의 PATCH/POST 에는 이게 없었다
// ============================================================

/// 🔴 정본은 `Notification::TYPES` (app/models/notification.rb) 다. 이 목록이
/// 모자라면 서버가 보낸 알림이 화면에서 "새 알림" 으로만 뭉뚱그려진다 —
/// 실제로 4종(new_follower · badge_earned · level_up · reputation_milestone)이
/// 그 상태였다. 모델에 타입을 추가하면 여기와 `Index.svelte` 의 문구 맵도 같이 늘린다.
export type NotificationType =
  | 'post_like'
  | 'post_comment'
  | 'comment_reply'
  | 'mention'
  | 'follow'
  | 'new_follower'
  | 'dm_received'
  | 'system_notice'
  | 'report_resolved'
  | 'report_received'
  | 'moderation_action'
  | 'badge_earned'
  | 'level_up'
  | 'reputation_milestone';

export type NotificationStatus = 'unread' | 'read' | 'archived';

// 🔴 필드명이 camelCase 인 것은 오타가 아니다 — api 클라이언트가 변환한 뒤의
//    모양이다. 서버 계약(snake_case)은 API_CONTRACT.md 가 정본이다.
export interface Notification {
  id: string;
  type: NotificationType;
  status: NotificationStatus;
  actor: { id: string; name: string; avatarUrl: string | null } | null;
  notifiableType: string | null;
  notifiableId: string | null;
  metadata: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  perPage: number;
  hasMore: boolean;
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  page: number;
  perPage: number;
}

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  hasMore: true,
  page: 1,
  perPage: 20,
};

function createNotificationsStore() {
  const { subscribe, set, update } = writable<NotificationsState>(initialState);

  return {
    subscribe,

    async fetch(reset = false) {
      update((s) => ({
        ...s,
        isLoading: true,
        error: null,
        ...(reset ? { notifications: [], page: 1, hasMore: true } : {}),
      }));

      let state!: NotificationsState;
      subscribe((s) => { state = s; })();

      if (!state.hasMore && !reset) {
        update((s) => ({ ...s, isLoading: false }));
        return;
      }

      const res = await api.get<{ notifications: Notification[]; pagination?: Pagination }>(
        '/api/v1/notifications',
        { params: { page: state.page, limit: state.perPage } },
      );

      if (!res.success) {
        // 🔴 조용히 넘어가지 않는다. 이전 구현은 예외를 삼켜서, 목록이 비어 보이는
        //    것이 "알림 없음" 인지 "불러오기 실패" 인지 화면에서 구분되지 않았다.
        update((s) => ({ ...s, isLoading: false, error: res.message ?? '알림을 불러오지 못했습니다.' }));
        return;
      }

      const payload = res.data;
      const list = payload?.notifications ?? [];
      const pagination = payload?.pagination;

      update((s) => {
        const merged = reset ? list : [...s.notifications, ...list];
        const hasMore = pagination ? !!pagination.hasMore : list.length === s.perPage;

        return {
          ...s,
          notifications: merged,
          hasMore,
          page: hasMore ? s.page + 1 : s.page,
          isLoading: false,
        };
      });

      await this.refreshUnreadCount();
    },

    async refreshUnreadCount() {
      const res = await api.get<{ count: number }>('/api/v1/notifications/unread_count');
      if (!res.success) return;
      const count = res.data?.count;
      if (typeof count !== 'number') return;
      update((s) => ({ ...s, unreadCount: count }));
    },

    async markAsRead(id: string) {
      // optimistic update — 실패하면 서버 상태로 되감는다.
      let wasUnread = false;
      update((s) => {
        wasUnread = s.notifications.some((n) => n.id === id && n.status === 'unread');
        return {
          ...s,
          notifications: s.notifications.map((n) => (n.id === id ? { ...n, status: 'read' } : n)),
          unreadCount: wasUnread ? Math.max(0, s.unreadCount - 1) : s.unreadCount,
        };
      });

      // 🔴 이미 읽은 알림을 다시 눌렀다고 서버를 때리지 않는다. 목록은 클릭이
      //    곧 이동이라 재클릭이 잦다.
      if (!wasUnread) return;

      const res = await api.patch(`/api/v1/notifications/${id}/read`, {});
      if (!res.success) await this.fetch(true);
    },

    async markAllAsRead() {
      update((s) => ({
        ...s,
        notifications: s.notifications.map((n) => ({ ...n, status: 'read' })),
        unreadCount: 0,
      }));

      const res = await api.post('/api/v1/notifications/mark_all_read', {});
      if (!res.success) await this.fetch(true);
    },

    addNotification(notification: Notification) {
      update((s) => ({
        ...s,
        notifications: [notification, ...s.notifications],
        unreadCount: s.unreadCount + (notification.status === 'unread' ? 1 : 0),
      }));
    },

    updateCount(count: number) {
      update((s) => ({ ...s, unreadCount: Math.max(0, count) }));
    },

    reset() {
      set(initialState);
    },
  };
}

export const notifications = createNotificationsStore();

export const unreadCount = derived(notifications, ($n) => $n.unreadCount);
